class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', FacetFiltersForm.onSizeAvailabilityChange);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    facetForm.addEventListener('click', (event) => {
      const clearBtn = event.target.closest('[data-vivi-size-avail-clear]');
      if (!clearBtn) return;
      event.preventDefault();
      FacetFiltersForm.clearSizeAvailability(facetForm);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    const bootSizeAvail = () => {
      const current = window.location.search.slice(1);
      const normalized = FacetFiltersForm.normalizeSizeAvailability(current);
      const params = new URLSearchParams(current);
      const hasSize = params.getAll('filter.v.option.size').some(Boolean);
      const hasAvail = params.getAll('filter.v.availability').includes('1');
      if (hasSize && !hasAvail && normalized !== current) {
        FacetFiltersForm.renderPage(normalized, null, true);
        return;
      }
      FacetFiltersForm.syncSizeAvailabilityUI();
      FacetFiltersForm.applyVariantSizeAvailability();
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootSizeAvail);
    } else {
      bootSizeAvail();
    }
  }

  /* VIVI: Available in size = option size + in-stock (availability=1) */
  static normalizeSizeAvailability(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    const sizes = params.getAll('filter.v.option.size').filter(Boolean);
    params.delete('filter.v.option.size');
    params.delete('filter.v.availability');
    sizes.forEach((size) => params.append('filter.v.option.size', size));
    if (sizes.length) {
      params.append('filter.v.availability', '1');
    }
    return params.toString();
  }

  static onSizeAvailabilityChange(event) {
    const input = event.target;
    if (!input || input.name !== 'filter.v.option.size' || input.type !== 'checkbox') return;
    if (input.checked) {
      document.querySelectorAll('input[name="filter.v.option.size"]').forEach((el) => {
        if (el !== input) el.checked = false;
      });
    }
  }

  static clearSizeAvailability(form) {
    const scope = form || document;
    scope.querySelectorAll('input[name="filter.v.option.size"]').forEach((el) => {
      el.checked = false;
    });
    const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev || window.location.search.slice(1));
    params.delete('filter.v.option.size');
    params.delete('filter.v.availability');
    FacetFiltersForm.renderPage(params.toString(), null, true);
  }

  static syncSizeAvailabilityUI() {
    const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev || window.location.search.slice(1));
    const sizes = params.getAll('filter.v.option.size').filter(Boolean);
    const activeSize = sizes[0] || '';

    document.querySelectorAll('input[name="filter.v.option.size"]').forEach((input) => {
      input.checked = Boolean(activeSize) && input.value === activeSize;
      const label = input.closest('label');
      if (label) label.classList.toggle('active', input.checked);
    });

    const clearParams = new URLSearchParams(params.toString());
    clearParams.delete('filter.v.option.size');
    clearParams.delete('filter.v.availability');
    const clearQuery = clearParams.toString();
    const clearHref = `${window.location.pathname}${clearQuery ? `?${clearQuery}` : ''}`;

    document.querySelectorAll('[data-vivi-size-avail-chip]').forEach((chip) => {
      const hasNativeSizeChip = Boolean(
        chip.parentElement &&
          [...chip.parentElement.querySelectorAll('.active-facets__button-inner')]
            .filter((el) => !el.closest('[data-vivi-size-avail-chip]'))
            .some((el) => /Available in\s*:/i.test(el.textContent || ''))
      );
      if (!activeSize || hasNativeSizeChip) {
        chip.setAttribute('hidden', '');
        return;
      }
      chip.removeAttribute('hidden');
      const label = chip.querySelector('[data-vivi-size-avail-chip-label]');
      if (label) label.textContent = activeSize;
      const link = chip.querySelector('[data-vivi-size-avail-chip-link]');
      if (link) link.href = clearHref;
    });

    const hasFilters = [...params.keys()].some((key) => key.startsWith('filter.'));
    document.querySelectorAll('[data-vivi-plp-clear-all]').forEach((el) => {
      if (hasFilters) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
      const link = el.querySelector('a');
      if (!link) return;
      const base = new URLSearchParams();
      const q = params.get('q');
      const sortBy = params.get('sort_by');
      if (q) base.set('q', q);
      if (sortBy) base.set('sort_by', sortBy);
      const qs = base.toString();
      link.href = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    });
  }

  /* Shopify size+availability is product-level; enforce real in-stock-for-size on cards */
  static parseSizeAvailMap(raw) {
    const map = {};
    if (!raw) return map;
    String(raw)
      .split('|')
      .forEach((part) => {
        const [size, flag] = part.split(':');
        if (!size) return;
        if (flag === '1') map[size] = true;
        else if (map[size] !== true) map[size] = false;
      });
    return map;
  }

  static cardAvailableInSize(cardEl, size) {
    if (!cardEl || !size) return true;
    const wrap = cardEl.querySelector('[data-vivi-size-avail]') || cardEl.closest('[data-vivi-size-avail]') || cardEl;
    const raw = wrap.getAttribute('data-vivi-size-avail');
    if (raw) {
      const map = FacetFiltersForm.parseSizeAvailMap(raw);
      if (Object.prototype.hasOwnProperty.call(map, size)) return map[size] === true;
    }
    const jsonEl = cardEl.querySelector('[data-vivi-card-product-json]');
    if (!jsonEl) return true;
    try {
      const data = JSON.parse(jsonEl.textContent);
      const opts = data.options || [];
      let sizeIndex = 0;
      opts.forEach((opt, i) => {
        const name = typeof opt === 'string' ? opt : opt && opt.name;
        if (name && String(name).toLowerCase() === 'size') sizeIndex = i;
      });
      const key = `option${sizeIndex + 1}`;
      return (data.variants || []).some((v) => v && v[key] === size && v.available);
    } catch (e) {
      return true;
    }
  }

  static applyVariantSizeAvailability() {
    const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev || window.location.search.slice(1));
    const activeSize = params.getAll('filter.v.option.size').filter(Boolean)[0] || '';
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    let visible = 0;
    grid.querySelectorAll(':scope > .grid__item').forEach((item) => {
      const card = item.querySelector('.vivi-card-wrap, .vivi-card, [data-vivi-size-avail]') || item;
      const ok = !activeSize || FacetFiltersForm.cardAvailableInSize(card, activeSize);
      item.hidden = !ok;
      item.style.display = ok ? '' : 'none';
      if (ok) visible += 1;
    });

    let empty = grid.parentElement && grid.parentElement.querySelector('[data-vivi-size-avail-empty]');
    if (activeSize && visible === 0) {
      if (!empty) {
        empty = document.createElement('div');
        empty.className = 'collection collection--empty title-wrapper center';
        empty.setAttribute('data-vivi-size-avail-empty', '');
        empty.innerHTML =
          '<h2 class="title title--primary">No products available in that size.<br><a class="underlined-link link" href="' +
          window.location.pathname +
          '">Clear filters</a></h2>';
        grid.insertAdjacentElement('afterend', empty);
      }
      empty.hidden = false;
    } else if (empty) {
      empty.hidden = true;
    }

    const countLabel =
      visible === 1 ? '1 product' : `${visible} products`;
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el && activeSize) el.textContent = countLabel;
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSizeAvailability(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    /* VIVI: re-bind card gallery + quick add + wishlist after facets AJAX morph */
    const grid = document.getElementById('ProductGridContainer');
    if (window.ViviCardGallery && typeof window.ViviCardGallery.scan === 'function') {
      window.ViviCardGallery.scan(grid || document);
    }
    if (window.ViviCardQuickAdd && typeof window.ViviCardQuickAdd.scan === 'function') {
      window.ViviCardQuickAdd.scan(grid || document);
    }
    if (window.ViviWishlist && typeof window.ViviWishlist.sync === 'function') {
      window.ViviWishlist.sync();
    }
    FacetFiltersForm.syncSizeAvailabilityUI();
    FacetFiltersForm.applyVariantSizeAvailability();
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.syncSizeAvailabilityUI();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const submittedForm = event.target.closest('form');
      const submittedId = submittedForm && submittedForm.id;

      /* Drawer mode: Sort pill must keep active filters; filter Apply must keep sort_by */
      if (submittedId === 'FacetSortDrawerForm') {
        const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev || '');
        const sortParams = new URLSearchParams(this.createSearchParams(submittedForm));
        const sortBy = sortParams.get('sort_by');
        if (sortBy) {
          params.set('sort_by', sortBy);
        } else {
          params.delete('sort_by');
        }
        this.onSubmitForm(params.toString(), event);
        return;
      }

      if (submittedId === 'FacetFiltersFormMobile') {
        const params = new URLSearchParams(this.createSearchParams(submittedForm));
        const prev = new URLSearchParams(FacetFiltersForm.searchParamsPrev || '');
        const sortBy = prev.get('sort_by');
        if (sortBy) params.set('sort_by', sortBy);
        this.onSubmitForm(params.toString(), event);
        return;
      }

      const forms = [];
      const isMobile = submittedId === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
